import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {LayoutPortal} from '../../../LayoutPortal';
import {LocalStorage} from '../../../utils/LocalStorage';
import {Spinner} from '../../Spinner';
import {PortalInformesApi} from '../../../api/portalInformesApi';
import {PortalReportNav} from '../reports/PortalReportNav';
import {PortalDateRangePicker} from '../reports/PortalDateRangePicker';
import {usePortalClientId} from '../../../hooks/usePortalClientId';
import {PortalInformeRenderer} from './PortalInformeRenderer';
import {INFORME_PORTAL_PERIODS} from './portalPeriods';

/** `settings.period` (snake_case persistido) → el shape del picker (camelCase). */
export function requestedInformeId(search = (typeof window !== 'undefined' ? window.location.search : '')) {
    try {
        return new URLSearchParams(search).get('client_report') || null;
    } catch (e) {
        return null;
    }
}

export function periodToDateRange(period) {
    if (!period || typeof period !== 'object') return null;
    if (period.type === 'FIXED' && period.start_date && period.end_date) return {startDate: period.start_date, endDate: period.end_date};
    if (period.type === 'RELATIVE' && period.predefined_period) return {predefinedPeriod: period.predefined_period};
    return null;
}

/**
 * Portal del Cliente — modalidad Informes (la app `reports`). Misma estructura que el landing
 * de Reportes V1 (topbar editorial + menú horizontal si hay 2+ + informe fullscreen + el
 * cliente sólo cambia el rango de fechas), con el renderer de paneles en sólo lectura.
 *
 * Props opcionales (preview de agencia): `listFn()` y `queryFn(informeId, dateRangeOverride)`.
 */
export function PortalInformesLanding({listFn, queryFn} = {}) {
    const {t} = useTranslation();
    const activeClientId = usePortalClientId();
    // Estable entre renders: el renderer lleva `queryFn` en las deps de su efecto de query.
    const defaultQueryFn = useCallback(
        (informeId, dateRangeOverride) => PortalInformesApi.renderMine(informeId, dateRangeOverride, activeClientId),
        [activeClientId],
    );
    const effectiveQueryFn = queryFn || defaultQueryFn;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [informes, setInformes] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [dateRangeOverride, setDateRangeOverride] = useState(null);

    useEffect(() => {
        setLoading(true);
        const fetcher = listFn || (() => PortalInformesApi.listMine(activeClientId));
        fetcher()
            .then((response) => {
                if (!response || response.status !== 'OK') {
                    setError(t('portal_informes.access_error', 'No pudimos cargar tus informes. Prueba a refrescar en un momento.'));
                    setLoading(false);
                    return;
                }
                const list = (response.data && response.data.client_reports) || [];
                setInformes(list);
                // Deep-link (`?client_report=<uuid>`, el que entrega el MCP y el botón «Ver como
                // cliente»): si está en la lista se abre ése; si no, el primero, sin revelar nada.
                const wanted = requestedInformeId();
                if (list.length > 0) setActiveId(list.some((i) => i.id === wanted) ? wanted : list[0].id);
                setLoading(false);
            })
            .catch(() => { setError(t('portal_informes.access_error', 'No pudimos cargar tus informes. Prueba a refrescar en un momento.')); setLoading(false); });
    }, [t, listFn, activeClientId]);

    const active = useMemo(() => informes.find((i) => i.id === activeId) || null, [informes, activeId]);
    const compareValue = useMemo(() => (active && active.settings && active.settings.compare ? periodToDateRange(active.settings.compare_period) : null), [active]);
    useEffect(() => { if (active) setDateRangeOverride(periodToDateRange(active.settings && active.settings.period)); }, [active]);

    const agencyName = LocalStorage.fetchAgencyDisplayName ? LocalStorage.fetchAgencyDisplayName() : '';
    const clientName = (LocalStorage.fetchUserFullName && LocalStorage.fetchUserFullName()) || '';
    const isSnapshot = !!(active && active.mode === 'SNAPSHOT');

    const datePicker = isSnapshot ? null : (
        <PortalDateRangePicker value={dateRangeOverride} compareValue={compareValue}
                               periods={INFORME_PORTAL_PERIODS} onChange={setDateRangeOverride}/>
    );
    const showStateHeader = loading || error || (!active && informes.length === 0);

    return (
        <LayoutPortal>
            <div className="portal-editorial" data-testid="portal-informes-landing">
                {showStateHeader && (
                    <header className="portal-editorial-header -minimal">
                        <div className="portal-editorial-header-text">
                            <span className="portal-editorial-eyebrow">{t('portal_informes.section_eyebrow', 'INFORMES')}</span>
                            {clientName && <h1 className="portal-editorial-title">{clientName}</h1>}
                        </div>
                    </header>
                )}
                {informes.length > 1 && <PortalReportNav reports={informes} activeReportId={activeId} onSelect={setActiveId}/>}
                <main className="portal-editorial-main">
                    {loading && <div className="portal-editorial-loading"><Spinner/></div>}
                    {!loading && error && (
                        <div className="portal-editorial-empty">
                            <span className="portal-editorial-eyebrow -error">{t('portal_informes.error_eyebrow', 'NO PUDIMOS CARGAR')}</span>
                            <h2 className="portal-editorial-empty-title">{t('portal_informes.error_title', 'Algo no anduvo bien.')}</h2>
                            <p className="portal-editorial-empty-body">{error}</p>
                        </div>
                    )}
                    {!loading && !error && informes.length === 0 && (
                        <div className="portal-editorial-empty">
                            <span className="portal-editorial-eyebrow">{t('portal_informes.empty_eyebrow', 'TU PORTAL')}</span>
                            <h2 className="portal-editorial-empty-title">{t('portal_informes.empty_title', 'Estamos preparando tu portal.')}</h2>
                            <p className="portal-editorial-empty-body">{t('portal_informes.empty_body', {agency: agencyName || t('portal_informes.agency_fallback', 'Tu agencia'), interpolation: {escapeValue: false}, defaultValue: '{{agency}} todavía no te asignó informes. Cuando lo haga, los verás aquí.'})}</p>
                        </div>
                    )}
                    {!loading && !error && active && (
                        <PortalInformeRenderer informe={active} dateRangeOverride={dateRangeOverride} queryFn={effectiveQueryFn} headerRight={datePicker}/>
                    )}
                </main>
            </div>
        </LayoutPortal>
    );
}
